import type { ProFormColumnsType } from '@ant-design/pro-components';
import type { BtlApplication } from '@api/btlApplicationForm/types';
import type { ExistingFirmResponse } from '@api/firm/firm.types';
import { App } from 'antd';
import { firmApi } from '@api/firm/firm.api';
import { userApi } from '@api/user/userApi';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { FormValidationContext } from '@hooks/useBtlFormValidation';
import { useBtlApplicationInProgressById } from '@queries/btlApplications/useApplicationInProgressById/useBtlApplicationInProgressById';
import { useBrokerFirm } from '@queries/broker/useBrokerFirm';
import { useAuth } from '@providers/AuthProvider/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { isDipAcceptedOrLater } from '../constants';
import { makeFormReadonly } from '../makeFormReadonly';
import type { BaseFormSchemaProps } from '../types';
import { useBtlFormMappedRoutes } from '../useBtlFormMappedRoutes';
import { hydrateFinancingAndPropertyFormValues } from './mapApplicationToFormValues';
import { submitFinancingAndPropertyForm } from './submitFinancingAndPropertyForm';

interface FinancingAndPropertySchemaProps extends BaseFormSchemaProps {
  isDipAccepted?: boolean;
  isDipAcceptedOrLater?: boolean;
  firm?: ExistingFirmResponse | null;
}

interface UseFinancingAndPropertyFormParams {
  formName: string;
  getFormSchema: (schemaProps: FinancingAndPropertySchemaProps) => ProFormColumnsType[];
}

function useFinancingAndPropertyFormSchemaProps(): FinancingAndPropertySchemaProps & {
  data?: BtlApplication;
  isLoading: boolean;
} {
  const { formatMessage } = useIntl();
  const { id = '' } = useParams();
  const { data, isLoading: isApplicationLoading } = useBtlApplicationInProgressById(id);
  const { data: firm, isLoading: isFirmLoading } = useBrokerFirm();
  const { isFormEditable } = useBtlFormMappedRoutes(data);

  const isDipAcceptedOrLaterStatus = isDipAcceptedOrLater(data?.status);

  return {
    formatMessage,
    data,
    firm,
    isLoading: isApplicationLoading || isFirmLoading,
    isFormEditable,
    isDipAccepted: data?.status === 'DIP_ACCEPTED',
    isDipAcceptedOrLater: isDipAcceptedOrLaterStatus,
  };
}

export function useFinancingAndPropertyForm({ formName, getFormSchema }: UseFinancingAndPropertyFormParams) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { id = '' } = useParams();
  const { isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const schemaProps = useFinancingAndPropertyFormSchemaProps();
  const { data, firm, isLoading, isFormEditable } = schemaProps;
  const propertyId = data?.applicationProperty?.property?.id;
  const { nextSection: section = '' } = useBtlFormMappedRoutes(data);
  const formValidationContext = useContext(FormValidationContext);

  if (!formValidationContext) {
    throw new Error('Financing and property forms must be used within a FormValidationProvider');
  }

  const { setIsSubmitting } = formValidationContext;

  if (isLoading || isFormEditable === undefined) {
    return { isLoading: true as const };
  }

  const formSchema = getFormSchema(schemaProps);
  const adjustedFormSchema = isFormEditable ? formSchema : makeFormReadonly(formSchema);

  return {
    isLoading: false as const,
    formProps: {
      columns: adjustedFormSchema,
      isFormEditable,
      name: formName,
      request: async () => {
        let brokerReferrerId: string | undefined;

        if (isSignedIn) {
          try {
            const user = await userApi.getUser();
            if (user?.id) {
              const brokerReferrer = await firmApi.getBrokerReferrerByUserId(user.id);
              brokerReferrerId = brokerReferrer.id;
            }
          } catch {
            brokerReferrerId = undefined;
          }
        }

        return hydrateFinancingAndPropertyFormValues(data, firm, brokerReferrerId);
      },
      onFinish: async (values: Record<string, unknown>) => {
        await submitFinancingAndPropertyForm({
          values,
          data,
          firm,
          id,
          propertyId,
          nextSection: section,
          navigate,
          applicationType: 'BUY_TO_LET',
          queryClient,
          setIsSubmitting,
          message,
        });
      },
    },
  };
}
